import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { Button } from '../../components/Button.js';
import { ReportModal } from '../../components/ReportModal.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { MAX_COMMENT_LENGTH } from '@ridehub/shared';

interface Comment {
  id: string;
  body: string;
  createdAt: string;
  parentId: string | null;
  user: { id: string; nickname: string; avatarUrl?: string };
}

interface CommentThread {
  comment: Comment;
  replies: Comment[];
}

function buildThreads(comments: Comment[]): CommentThread[] {
  const roots = comments.filter((c) => c.parentId === null);
  const replyMap = new Map<string, Comment[]>();
  for (const c of comments) {
    if (c.parentId) {
      const existing = replyMap.get(c.parentId) ?? [];
      replyMap.set(c.parentId, [...existing, c]);
    }
  }
  return roots.map((root) => ({
    comment: root,
    replies: (replyMap.get(root.id) ?? []).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    ),
  }));
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Avatar({ user }: { user: Comment['user'] }) {
  return (
    <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0 overflow-hidden">
      {user.avatarUrl && <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />}
    </div>
  );
}

interface CommentSectionProps {
  spotId: string;
  initialComments?: unknown[] | null;
  onCountChange?: (count: number) => void;
  targetCommentId?: string;
}

export function CommentSection({
  spotId,
  initialComments,
  onCountChange,
  targetCommentId,
}: CommentSectionProps) {
  const { user, dbUser } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // 返信フォームを開いているコメントID
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  // 返信を折りたたんでいるルートコメントIDのセット（デフォルト展開）
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  // 未ログイン時の返信ヒント
  const [loginHintVisible, setLoginHintVisible] = useState(false);
  const loginHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 対象コメントへのスクロール・ハイライト
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const scrolledToTarget = useRef(false);

  useEffect(() => {
    if (initialComments != null) {
      const loaded = initialComments as Comment[];
      setComments(loaded);
      onCountChange?.(loaded.length);
      return;
    }
    apiClient.get<Comment[]>(`/spots/${spotId}/comments`).then((data) => {
      const loaded = data ?? [];
      setComments(loaded);
      onCountChange?.(loaded.length);
    });
  }, [spotId, onCountChange, initialComments]);

  useEffect(() => {
    if (!targetCommentId || comments.length === 0 || scrolledToTarget.current) return;

    // 返信の場合は親スレッドを展開しておく
    const target = comments.find((c) => c.id === targetCommentId);
    if (target?.parentId) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        next.delete(target.parentId!);
        return next;
      });
    }

    const timer = setTimeout(() => {
      const el = document.getElementById(`comment-${targetCommentId}`);
      if (el) {
        scrolledToTarget.current = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedCommentId(targetCommentId);
        setTimeout(() => setHighlightedCommentId(null), 2500);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [targetCommentId, comments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const comment = await apiClient.post<Comment>(
        `/spots/${spotId}/comments`,
        { body },
        { requireAuth: true }
      );
      if (comment) {
        setComments((prev) => {
          const next = [comment, ...prev];
          onCountChange?.(next.length);
          return next;
        });
        setBody('');
      }
    } catch {
      setError('コメントの投稿に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (parentId: string) => {
    if (!replyBody.trim()) return;
    setReplySubmitting(true);
    setReplyError(null);
    try {
      const comment = await apiClient.post<Comment>(
        `/spots/${spotId}/comments`,
        { body: replyBody, parentId },
        { requireAuth: true }
      );
      if (comment) {
        setComments((prev) => {
          const next = [...prev, comment];
          onCountChange?.(next.length);
          return next;
        });
        setReplyBody('');
        setReplyingToId(null);
      }
    } catch {
      setReplyError('返信の投稿に失敗しました');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await apiClient.delete(`/comments/${commentId}`, { requireAuth: true });
      setComments((prev) => {
        const next = prev.filter((c) => c.id !== commentId);
        onCountChange?.(next.length);
        return next;
      });
    } catch {
      // エラー無視
    }
  };

  const handleUnauthReply = () => {
    if (loginHintTimerRef.current) clearTimeout(loginHintTimerRef.current);
    setLoginHintVisible(true);
    loginHintTimerRef.current = setTimeout(() => setLoginHintVisible(false), 3000);
  };

  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const threads = buildThreads(comments);

  return (
    <div>
      <h3 className="font-bold text-lg mb-3">コメント ({comments.length})</h3>

      {/* 新規コメント投稿フォーム */}
      {user && (
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="relative">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={MAX_COMMENT_LENGTH}
              rows={3}
              placeholder="コメントを入力..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <span className="absolute bottom-2 right-2 text-xs text-gray-400">
              {body.length}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
          {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          <Button
            type="submit"
            size="sm"
            className="mt-2"
            loading={submitting}
            disabled={!body.trim()}
          >
            投稿する
          </Button>
        </form>
      )}

      {/* コメントスレッド一覧 */}
      <ul className="space-y-4">
        {threads.map(({ comment, replies }) => {
          const isCollapsed = collapsedIds.has(comment.id);
          const isReplyOpen = replyingToId === comment.id;

          return (
            <li key={comment.id} id={`comment-${comment.id}`}>
              {/* ルートコメント */}
              <div
                className={`flex gap-3 rounded-lg p-1 -m-1 transition-colors duration-[2000ms] ${
                  highlightedCommentId === comment.id ? 'bg-amber-50' : ''
                }`}
              >
                <Avatar user={comment.user} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{comment.user.nickname}</span>
                    <span className="text-xs text-gray-400">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">{comment.body}</p>
                  <div className="flex gap-3 mt-1.5 items-center flex-wrap">
                    {/* 返信ボタン */}
                    {user ? (
                      <button
                        onClick={() => {
                          setReplyingToId(isReplyOpen ? null : comment.id);
                          setReplyBody('');
                          setReplyError(null);
                        }}
                        className="text-xs text-primary hover:text-primary/70 font-medium"
                      >
                        {isReplyOpen ? 'キャンセル' : '返信'}
                      </button>
                    ) : (
                      <button
                        onClick={handleUnauthReply}
                        className="text-xs text-primary hover:text-primary/70 font-medium"
                      >
                        返信
                      </button>
                    )}
                    {/* 件数トグル */}
                    {replies.length > 0 && (
                      <button
                        onClick={() => toggleCollapse(comment.id)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        {isCollapsed
                          ? `▶ ${replies.length}件の返信`
                          : `▼ ${replies.length}件の返信`}
                      </button>
                    )}
                    {/* 削除・通報 */}
                    {dbUser?.id === comment.user.id && (
                      <button
                        onClick={() => setDeletingCommentId(comment.id)}
                        className="text-xs text-gray-400 hover:text-red-500"
                      >
                        削除
                      </button>
                    )}
                    {user && dbUser?.id !== comment.user.id && (
                      <button
                        onClick={() => setReportingCommentId(comment.id)}
                        className="text-xs text-gray-400 hover:text-red-500"
                      >
                        通報
                      </button>
                    )}
                  </div>

                  {/* インライン返信フォーム */}
                  {isReplyOpen && (
                    <div className="mt-2">
                      <div className="relative">
                        <textarea
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          maxLength={MAX_COMMENT_LENGTH}
                          rows={2}
                          placeholder={`${comment.user.nickname}さんへの返信...`}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                          autoFocus
                        />
                        <span className="absolute bottom-2 right-2 text-xs text-gray-400">
                          {replyBody.length}/{MAX_COMMENT_LENGTH}
                        </span>
                      </div>
                      {replyError && <p className="text-sm text-red-500 mt-1">{replyError}</p>}
                      <Button
                        size="sm"
                        className="mt-1.5"
                        loading={replySubmitting}
                        disabled={!replyBody.trim()}
                        onClick={() => void handleReplySubmit(comment.id)}
                      >
                        返信する
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* 返信リスト（折りたたみ対応） */}
              {!isCollapsed && replies.length > 0 && (
                <ul className="ml-8 mt-2 space-y-3 border-l-2 border-gray-100 pl-3">
                  {replies.map((reply) => (
                    <li key={reply.id} id={`comment-${reply.id}`}>
                      <div
                        className={`flex gap-3 rounded-lg p-1 -m-1 transition-colors duration-[2000ms] ${
                          highlightedCommentId === reply.id ? 'bg-amber-50' : ''
                        }`}
                      >
                        <Avatar user={reply.user} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{reply.user.nickname}</span>
                            <span className="text-xs text-gray-400">
                              {formatDate(reply.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5">{reply.body}</p>
                          <div className="flex gap-3 mt-1">
                            {dbUser?.id === reply.user.id && (
                              <button
                                onClick={() => setDeletingCommentId(reply.id)}
                                className="text-xs text-gray-400 hover:text-red-500"
                              >
                                削除
                              </button>
                            )}
                            {user && dbUser?.id !== reply.user.id && (
                              <button
                                onClick={() => setReportingCommentId(reply.id)}
                                className="text-xs text-gray-400 hover:text-red-500"
                              >
                                通報
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}

        {threads.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-10 text-gray-400">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-sm text-center">
              コメントはまだありません
              <br />
              <span className="text-xs">最初のコメントを投稿しよう</span>
            </p>
          </div>
        )}
      </ul>

      {/* 未ログイン返信ヒント */}
      {loginHintVisible && (
        <div
          className="fixed left-4 right-4 z-[3000] flex items-center justify-between bg-gray-900 text-white rounded-xl px-4 py-3 shadow-lg"
          style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px) + 8px)' }}
        >
          <span className="text-sm">ログインすると返信できます</span>
          <Link to="/login" className="text-sm font-medium text-primary-300 ml-3 whitespace-nowrap">
            ログイン →
          </Link>
        </div>
      )}

      {deletingCommentId && (
        <ConfirmDialog
          title="コメントを削除"
          message="このコメントを削除しますか？この操作は取り消せません。"
          onConfirm={() => {
            void handleDelete(deletingCommentId);
            setDeletingCommentId(null);
          }}
          onCancel={() => setDeletingCommentId(null)}
        />
      )}

      {reportingCommentId && (
        <ReportModal
          targetType="comment"
          targetId={reportingCommentId}
          onClose={() => setReportingCommentId(null)}
        />
      )}
    </div>
  );
}
